import {Injectable} from '@angular/core';
import {BaseJsonService} from '@shared/services/base.service';
import {Observable} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {ListResponse, ObjectResponse} from '@shared/models';
import {Pagination} from 'src/models/general/pagination.model';
import {ListQueryParameter} from 'src/models/general/list.query.parameter.model';
import {PreviewDataEntry, PreviewDataFormEntry} from 'src/models/productImport/previewData.model';
import {
	ImportConfigCreateModel,
	ImportConfigDetailModel,
	ImportConfigListModel
} from 'src/models/productImport/config/importConfig.model';
import {ImporterConfigMapping} from 'src/models/productImport/mapping/mapping.config.model';
import {OrderReviewDetailModel, OrderReviewModel} from 'src/models/productImport/orders/orderReview.model';
import {HttpParams, HttpResponse} from '@angular/common/http';
import {LlmModel, LlmPromptTemplate} from '../../models/productImport/llm.model';
import {SelectItem} from '../../models/general/select.item.model';
import {FtpConfig, FtpConnectionTestResult} from '../../models/productImport/config/email.config.model';
import { FailedDeliveriesResponse, FailedDeliveryGroup } from 'src/models/productImport/delivery/failed-delivery.model';


export interface ImportConfigListResponse {
	importer: ImportConfigListModel[];
	pagination: Pagination;
}

export interface OrderReviewListResponse {
	data: OrderReviewModel[];
	pagination: Pagination;
}


@Injectable({
	providedIn: 'root'
})
export class ImportConfigApiService extends BaseJsonService {

	getImportConfigList(params: ListQueryParameter): Observable<ImportConfigListResponse> {
		const url = this.generateUrl(this.services.importManager, 'importer/config');
		// Clone to not cache filtered searches
		const options = {...this.options};
		options.params = params.getHttpQueryParams();

		return this.http.get<ObjectResponse<ImportConfigListResponse>>(url, options).pipe(
			map((response: ObjectResponse<ImportConfigListResponse>) => {
				if (!response.data?.importer) {
					response.data.importer = [];
				}
				return response.data || [];
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	getImportConfig(importConfigUuid: string): Observable<ImportConfigDetailModel> {
		const url = this.generateUrl(this.services.importManager, '%s/%s', 'importer/config', importConfigUuid);
		return this.http.get<ObjectResponse<ImportConfigDetailModel>>(url, this.options).pipe(
			map(
				(res: ObjectResponse<ImportConfigDetailModel>) => {
					return res.data;
				}
			)
		);
	}

	downloadImportData(importConfigUuid: string): void {
		const url = this.generateUrl(this.services.importManager, '%s/%s', 'importer/download', importConfigUuid);
		this.http.get(url, {
			responseType: 'blob', // Set the response type to blob
			observe: 'response' // Include the full response object
		})
			.subscribe((response: HttpResponse<Blob>) => {
				const filename = this.getFilenameFromResponse(response);
				this.saveFile(response.body, filename);
			});
	}
	getLlmModels(): Observable<LlmModel[]> {
		const url = this.generateUrl(this.services.importManager, 'importer/llm-models');

		return this.http
			.get<{ models: LlmModel[] }>(url, this.options)
			.pipe(
				map(res => {
					console.log('RAW RESPONSE Model:', res);
					return res.models ?? [];
				}),
				catchError(BaseJsonService.handleError)
			);
	}
	getLlmPromptTemplate(): Observable<LlmPromptTemplate> {
		const url = this.generateUrl(this.services.importManager, 'importer/llm-prompt-template');

		return this.http.get<LlmPromptTemplate>(url, this.options)
			.pipe(
				tap(res => console.log('RAW RESPONSE Prompt:', res)),
				catchError(BaseJsonService.handleError)
			);
	}


	getFilenameFromResponse(response: any): string {
		const contentDisposition = response.headers.get('Content-Disposition');
		if (contentDisposition) {
			const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
			const matches = regex.exec(contentDisposition);
			if (matches && matches.length > 1) {
				return matches[1].replace(/['"]/g, '');
			}
		}
		return 'example.csv'; // Default filename if header extraction fails
	}

	saveFile(response: Blob | null, filename: string): void {
		if (response === null) {
			return;
		}
		const blob = new Blob([response], {type: 'application/octet-stream'});
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		link.click();
		window.URL.revokeObjectURL(url);
	}

	createNewImportConfig(formValues: ImportConfigCreateModel): Observable<ImportConfigDetailModel> {
		const url = this.generateUrl(this.services.importManager, '%s', 'importer/config');

		return this.http.post<ObjectResponse<ImportConfigDetailModel>>(url, formValues, this.options).pipe(
			map((response: ObjectResponse<ImportConfigDetailModel>) => {
				return response.data;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	updateImportConfig(importConfig: ImportConfigDetailModel): Observable<ImportConfigDetailModel> {
		const url = this.generateUrl(this.services.importManager, 'importer/config');

		return this.http.put<ObjectResponse<ImportConfigDetailModel>>(url, importConfig, this.options).pipe(
			map((response: ObjectResponse<ImportConfigDetailModel>) => {
				return response.data;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	deleteImportConfig(uuid: string): Observable<void> {
		const url = this.generateUrl(this.services.importManager, '%s/%s', 'importer/config', uuid);

		return this.http.delete(url, this.options).pipe(
			map(() => {
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	testConnectionForConfig(uuid: string | undefined): Observable<ObjectResponse<string>> {
		if (!uuid) {
			throw Error('No uuid given');
		}
		const url = this.generateUrl(this.services.importManager, '%s/%s', 'importer/connectionTest', uuid);

		return this.http.get<ObjectResponse<string>>(url, this.options).pipe(
			map((response: ObjectResponse<string>) => {
				return response;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	// FTP-Verbindungstest mit den (Klartext-)Formularwerten — POST /importer/ftp/test.
	// Prüft NICHT die gespeicherte (verschlüsselte) Config; der User testet, was er
	// gerade eingibt, bevor er speichert.
	testFtpConnection(cfg: FtpConfig): Observable<FtpConnectionTestResult> {
		const url = this.generateUrl(this.services.importManager, '%s', 'importer/ftp/test');
		return this.http.post<FtpConnectionTestResult>(url, cfg, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	// =========================================================================================================

	getPreviewDataForConfig(uuid: string): Observable<PreviewDataFormEntry[]> {
		if (!uuid) {
			throw Error('No uuid given');
		}

		const url = this.generateUrl(this.services.importManager, '%s/%s', 'importer/dataPreview', uuid);

		return this.http.get<ListResponse<PreviewDataEntry>>(url, this.options).pipe(
			map((response: ListResponse<PreviewDataEntry>) => {

				if (!response.data || response.data.length === 0) {
					throw Error(`Testdata Response contains no testdata-rows: ${response}`);
				}

				return Object.entries(response.data[0].values).map((entry: [string, any]) => {
					return {key: entry[0], value: entry[1]};
				});
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	// =========================================================================================================

	getMappingForConfig(importUuid: string): Observable<ImporterConfigMapping> {
		const url = this.generateUrl(this.services.importManager, '%s/%s', 'importer/mapping', importUuid);
		// return of({} as ImporterConfigMapping);
		return this.http.get<ObjectResponse<ImporterConfigMapping>>(url, this.options).pipe(
			map((response: ObjectResponse<ImporterConfigMapping>) => {
				return response.data;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	updateImportConfigMapping(updateModel: ImporterConfigMapping, importUuid: string | undefined): Observable<ImportConfigDetailModel> {
		if (!importUuid) {
			throw Error('No Importer UUID given for Mapping-Update');
		}

		const url = this.generateUrl(this.services.importManager, '%s/%s', 'importer/mapping', importUuid);

		return this.http.put<ObjectResponse<ImportConfigDetailModel>>(url, updateModel, this.options).pipe(
			map((response: ObjectResponse<ImportConfigDetailModel>) => {
				return response.data;
			}),
			catchError(BaseJsonService.handleError)
		);

	}

	runImport(uuid: string): Observable<ImportConfigDetailModel> {
		const url = this.generateUrl(this.services.importManager, '%s/%s', 'importer/trigger', uuid);

		return this.http.get<ObjectResponse<ImportConfigDetailModel>>(url, this.options).pipe(
			map((response: ObjectResponse<ImportConfigDetailModel>) => {
				return response.data;
			}),
			catchError(BaseJsonService.handleError)
		);

	}

	updateCronStatus(uuid: string, shouldBeEnabled: boolean): Observable<void> {
		const url = this.generateUrl(this.services.importManager, 'importer/cron/%s?shouldBeEnabled=%s', uuid, shouldBeEnabled);

		return this.http.put<void>(url, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	getOrderReviewList(params: ListQueryParameter): Observable<OrderReviewListResponse> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders/review');
		const options = {...this.options};
		options.params = params.getHttpQueryParams();

		return this.http.get<OrderReviewListResponse>(url, options).pipe(
			map((response: OrderReviewListResponse) => {
				console.log('#import-config# Order review list response:', response);
				return response;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	// Alle Orders (nicht nur Review-Queue). needsReview: true = nur geflaggte,
	// false = nur fehlerfreie, null = alle. Backend paginiert über limit/offset.
	getOrdersList(limit: number, offset: number, needsReview: boolean | null,
	              filters?: { orderNumber?: string; supplier?: string; sender?: string; reviewReasons?: string; date?: string },
	              duplicates: 'only' | 'exclude' | null = null): Observable<OrderReviewListResponse> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders');
		const options = {...this.options};
		let params = new HttpParams().set('limit', String(limit)).set('offset', String(offset));
		if (needsReview !== null) {
			params = params.set('needsReview', String(needsReview));
		}
		if (duplicates) {
			params = params.set('duplicates', duplicates);
		}
		// Server-seitige Filter (suchen über ALLE Orders, nicht nur die geladene Seite).
		if (filters) {
			if (filters.orderNumber) { params = params.set('orderNumber', filters.orderNumber); }
			if (filters.supplier) { params = params.set('supplier', filters.supplier); }
			if (filters.sender) { params = params.set('sender', filters.sender); }
			if (filters.reviewReasons) { params = params.set('reviewReasons', filters.reviewReasons); }
			if (filters.date) { params = params.set('date', filters.date); }
		}
		options.params = params;

		return this.http.get<OrderReviewListResponse>(url, options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	getOrderReviewDetail(id: string): Observable<OrderReviewDetailModel> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders/review/%s', id);

		return this.http.get<OrderReviewDetailModel>(url, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	updateOrderReview(id: string, data: OrderReviewDetailModel): Observable<void> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders/review/%s', id);

		return this.http.put<void>(url, data, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	getFailedDeliveries(limit: number = 25): Observable<FailedDeliveriesResponse> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders/failed');
		const options = {...this.options};
		options.params = new HttpParams().set('limit', String(limit));

		return this.http.get<FailedDeliveriesResponse>(url, options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	// Weitere Seite innerhalb EINES Importers nachladen.
	getFailedDeliveriesForConfig(configId: number, limit: number, offset: number): Observable<FailedDeliveryGroup> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders/failed');
		const options = {...this.options};
		options.params = new HttpParams()
			.set('configId', String(configId))
			.set('limit', String(limit))
			.set('offset', String(offset));

		return this.http.get<FailedDeliveryGroup>(url, options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	redriveOrder(id: string): Observable<void> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders/%s/redrive', id);

		return this.http.post<void>(url, {}, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	redriveAllOrders(): Observable<void> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders/redrive');

		return this.http.post<void>(url, {}, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	approveOrderReview(id: string): Observable<void> {
		const url = this.generateUrl(this.services.importManager, 'importer/orders/review/%s/approve', id);

		return this.http.post<void>(url, {}, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}
}
